import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import './display.css';

import { exportTransactionsToExcel } from "../../utils/exportToExcel";
import { calculateStatistics } from "../utils/Stat";
import { calculateHoldings, calculateInvestment, calculateUPL } from "../utils/Holding";
import { getFilteredTransactions, getUniqueScrips, getUniquePartners } from "../utils/filterUtils";

import TopBar from "./components/TopBar/TopBar";
import StatsTop from "./components/StatsTop/StatsTop";
import Holdings from "./components/Holdings/Holdings";
import TransactionsTable from "./components/TransactionTable/TransactionsTable";
import { useAuth } from "../../context/AuthContext";

const DisplayTransactions = ({
  externalTransactions = null,
  isPublic = false,
  profileUser = null,
  showToast,
  refreshVersion
}) => {

  const navigate = useNavigate();
  const { userId } = useAuth();

  const [transactions, setTransactions] = useState(null); // 🔥 IMPORTANT: null = not loaded yet
  const [filter, setFilter] = useState('all');
  const [selectedScrip, setSelectedScrip] = useState('all');
  const [holdings, setHoldings] = useState({});
  const [manualLtp, setManualLtp] = useState({});
  const [selectedPartner, setSelectedPartner] = useState({
    id: "all",
    name: "All Partners"
  });

  const [privacy, setPrivacy] = useState(
    profileUser?.privacy || "public"
  );

  // =============================
  // 1. LOAD DATA (ONLY EXTERNAL)
  // =============================
  useEffect(() => {
    if (externalTransactions === null) return;

    setTransactions(externalTransactions);

  }, [externalTransactions, refreshVersion]);

  // =============================
  // 2. HOLDINGS (DERIVED STATE)
  // =============================
  useEffect(() => {
    if (!transactions) return;

    const result = calculateHoldings(transactions, selectedPartner);
    setHoldings(result);

  }, [transactions, selectedPartner]);

  // =============================
  // 3. PROFILE PRIVACY SYNC
  // =============================
  useEffect(() => {
    if (profileUser?.privacy) {
      setPrivacy(profileUser.privacy);
    }
  }, [profileUser]);

  const isOwner =
    !profileUser ||
    profileUser.id === userId;

  const canEdit = isOwner;

  const handleEdit = (tx) => {
    if (!canEdit) return;

    navigate(
      `/${profileUser.username}/edit`,
      {
        state: { tx }
      }
    );
  };

  // =============================
  // 4. MEMOIZED DERIVATIONS
  // =============================
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    return getFilteredTransactions({
      transactions,
      filter,
      selectedScrip,
      selectedPartner
    });

  }, [transactions, filter, selectedScrip, selectedPartner]);

  const uniqueScrips = useMemo(() => {
    if (!transactions) return [];

    return getUniqueScrips(transactions, selectedPartner);

  }, [transactions, selectedPartner]);

  const uniquePartners = useMemo(() => {
    if (!transactions) return [];

    return getUniquePartners(transactions);

  }, [transactions]);

  const stats = useMemo(() => {
    if (!transactions) return null;

    return calculateStatistics({
      transactions,
      selectedScrip,
      selectedPartner,
      holdings
    });

  }, [transactions, selectedScrip, selectedPartner, holdings]);

  // reset scrip when partner changes
  useEffect(() => {
    setSelectedScrip("all");
  }, [selectedPartner]);

  // =============================
  // 5. ACTIONS
  // =============================
  const handleExport = () => {
    if (!isOwner || !transactions) return;

    exportTransactionsToExcel(filteredTransactions, "Transactions.xlsx");
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showToast("Link Copied !", "Success");
  };

  // =============================
  // 6. LOADING GATE (IMPORTANT FIX)
  // =============================
  if (transactions === null) {
    return (
      <div className="container">
        <div className="loading">Loading data...</div>
      </div>
    );
  }

  // =============================
  // 7. UI
  // =============================
  return (
    <div className="container">

      <TopBar
        profileUser={profileUser}
        handleShare={handleShare}

        userId={userId}
        privacy={privacy}
        setPrivacy={setPrivacy}

        filter={filter}
        selectedScrip={selectedScrip}
        selectedPartner={selectedPartner}

        uniqueScrips={uniqueScrips}
        uniquePartners={uniquePartners}

        setFilter={setFilter}
        setSelectedScrip={setSelectedScrip}
        setSelectedPartner={setSelectedPartner}
      />

      <div className="statistics-container">
        <StatsTop stats={stats} />

        <Holdings
          holdings={holdings}
          transactions={transactions}
          selectedPartner={selectedPartner}
          manualLtp={manualLtp}
          setManualLtp={setManualLtp}
          calculateInvestment={calculateInvestment}
          calculateUPL={calculateUPL}
        />
      </div>

      <TransactionsTable
        data={filteredTransactions}
        onEdit={canEdit ? handleEdit : null}
        canEdit={canEdit}
      />

      {isOwner && (
        <button className="export-btn" onClick={handleExport}>
          Export to Excel
        </button>
      )}

    </div>
  );
};

export default DisplayTransactions;